import React, { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './AdminPortal.css';
import { getActiveEvent, getAvailableTeams, getRegistrations, updateRegistration, deleteRegistration, addPlayerToTeam } from '../services/api';
import * as XLSX from 'xlsx';

const AdminPortal = () => {
  const { user } = useAuth();

  const [registrantRows, setRegistrantRows] = useState([]);
  const [availableTeamNames, setAvailableTeamNames] = useState([]);
  const [teamNameToIdMap, setTeamNameToIdMap] = useState({});
  const [teamInputs, setTeamInputs] = useState({});
  const [teamPlayers, setTeamPlayers] = useState({});
  const [, setPlayerInputs] = useState({});
  const [playerTeamInputs, setPlayerTeamInputs] = useState({});
  const [, setErrors] = useState({});
  const [, setPlayerErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [addPlayerForm, setAddPlayerForm] = useState({
    eventId: null,
    teamId: '',
    contactName: '',
    email: '',
    phone: '',
    comments: '',
  });
  const [addPlayerErrors, setAddPlayerErrors] = useState({});
  const [addPlayerSubmitting, setAddPlayerSubmitting] = useState(false);
  const [teamsList, setTeamsList] = useState([]);

  // Helper function to transform registrations and update state
  const processRegistrations = (registrations) => {
    // Transform API registrations to match the expected format
    const transformedRegistrants = registrations.map((reg, index) => ({
      id: String(reg.id || `REG-${String(index + 1).padStart(3, '0')}`), // Ensure ID is always a string
      teamName: reg.teamName || '',
      teamId: reg.teamId || null,
      contactName: reg.contactName || '',
      email: reg.email || '',
      phone: reg.phone || '',
      players: reg.numberOfPlayers || 0,
      comments: reg.comments || '',
      registeredAt: reg.registeredDate || new Date().toISOString().split('T')[0],
      playerNames: reg.playerNames || [], // Store player names from API
    }));

    setRegistrantRows(transformedRegistrants);

    // Initialize team inputs
    const inputs = transformedRegistrants.reduce((acc, row) => ({ ...acc, [row.id]: row.teamName }), {});
    setTeamInputs(inputs);

    // Initialize team players from registration data
    const players = transformedRegistrants.reduce((acc, row) => {
      const teamName = row.teamName || '';
      if (!teamName) return acc;

      const desiredCount = Number(row.players || 0);
      // Use playerNames from API if available, otherwise generate fallback names
      const apiPlayerNames = row.playerNames || [];
      const fallback = Array.from({ length: desiredCount }, (_, index) => {
        if (desiredCount === 1) return row.contactName;
        return apiPlayerNames[index] || `${row.contactName} ${index + 1}`;
      });
      
      const names = apiPlayerNames.length === desiredCount 
        ? apiPlayerNames 
        : fallback.map((name, idx) => apiPlayerNames[idx] || name || row.contactName);
      
      const playerObjects = names.map((name, index) => ({
        id: `${row.id}-P${index + 1}`,
        name: name || row.contactName,
      }));

      // Merge with existing players for the same team
      if (acc[teamName]) {
        acc[teamName] = [...acc[teamName], ...playerObjects];
      } else {
        acc[teamName] = playerObjects;
      }
      
      return acc;
    }, {});

    setTeamPlayers(players);
  };

  // Fetch registrations and teams from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const activeEvent = await getActiveEvent();
        if (activeEvent) {
          // Fetch teams
          try {
            const teams = await getAvailableTeams(activeEvent.id);
            const teamNames = teams.map(team => team.name);
            setAvailableTeamNames(teamNames);
            setTeamsList(teams); // Store full teams list for dropdown
            // Create mapping from team name to team id
            const nameToIdMap = teams.reduce((acc, team) => {
              if (team.name && team.id) {
                acc[team.name] = team.id;
              }
              return acc;
            }, {});
            setTeamNameToIdMap(nameToIdMap);
          } catch (err) {
            console.error('Error fetching teams:', err);
          }

          // Fetch registrations
          const registrations = await getRegistrations(activeEvent.id);
          processRegistrations(registrations || []);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        // Fallback to empty state on error
        setRegistrantRows([]);
        setTeamPlayers({});
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getSelectableTeams = (currentTeam) => {
    const names = new Set([
      ...availableTeamNames,
      ...Object.keys(teamPlayers),
      ...registrantRows.map((r) => r.teamName || ''),
    ]);
    return Array.from(names).filter(
      (name) => name && ((teamPlayerCounts[name] || 0) < 4 || name === currentTeam)
    );
  };

  const teamPlayerCounts = useMemo(
    () =>
      Object.entries(teamPlayers).reduce((acc, [team, players]) => {
        acc[team] = players.length;
        return acc;
      }, {}),
    [teamPlayers]
  );

  const teamMeta = useMemo(
    () => registrantRows.reduce((acc, row) => ({ ...acc, [row.teamName]: row }), {}),
    [registrantRows]
  );

  const playerRows = useMemo(
    () => {
      // Players from teams
      const teamPlayerRows = Object.entries(teamPlayers).flatMap(([teamName, players]) =>
        players.map((player, index) => ({
          id: player.id,
          playerLabel: player.name,
          teamName,
          contactName: teamMeta[teamName]?.contactName || '—',
          email: teamMeta[teamName]?.email || '—',
          phone: teamMeta[teamName]?.phone || '—',
          comments: teamMeta[teamName]?.comments || '—',
          idx: index + 1,
        }))
      );

      // Unassigned players (registrantRows without team names)
      const unassignedPlayerRows = registrantRows
        .filter((row) => !row.teamName && row.players > 0)
        .flatMap((row) => {
          const playerNames = row.playerNames || [];
          const desiredCount = Number(row.players || 0);
          const names = playerNames.length === desiredCount
            ? playerNames
            : Array.from({ length: desiredCount }, (_, index) => 
                playerNames[index] || (desiredCount === 1 ? row.contactName : `${row.contactName} ${index + 1}`)
              );
          
          return names.map((name, index) => ({
            id: `${row.id}-P${index + 1}`,
            playerLabel: name || row.contactName,
            teamName: '',
            contactName: row.contactName || '—',
            email: row.email || '—',
            phone: row.phone || '—',
            comments: row.comments || '—',
            idx: index + 1,
          }));
        });

      return [...teamPlayerRows, ...unassignedPlayerRows];
    },
    [teamPlayers, teamMeta, registrantRows]
  );

  // Get unique team names for filter dropdown
  const uniqueTeams = useMemo(() => {
    const teams = new Set(playerRows.map((p) => p.teamName).filter(Boolean));
    return Array.from(teams).sort();
  }, [playerRows]);

  // Filter and sort player rows
  const filteredAndSortedPlayerRows = useMemo(() => {
    let filtered = playerRows;

    // Filter by team
    if (selectedTeamFilter !== 'all') {
      if (selectedTeamFilter === 'unassigned') {
        filtered = filtered.filter((p) => !p.teamName);
      } else {
        filtered = filtered.filter((p) => p.teamName === selectedTeamFilter);
      }
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.playerLabel.toLowerCase().includes(query) ||
          p.teamName?.toLowerCase().includes(query) ||
          p.contactName?.toLowerCase().includes(query) ||
          p.email?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [playerRows, selectedTeamFilter, searchQuery]);

  const metrics = useMemo(() => {
    const totalPlayers = playerRows.length;
    const totalTeams = new Set(playerRows.map((p) => p.teamName || '').filter(Boolean)).size;
    const unassignedPlayers = playerRows.filter((p) => !p.teamName).length;
    const playerCapacity = 100;
    const teamCapacity = 25;
    const revenueCapacity = 18000;
    const progress = Math.min((totalPlayers / playerCapacity) * 100, 100);
    const revenue = totalPlayers * 180;
    const revenueProgress = Math.min((revenue / revenueCapacity) * 100, 100);

    return {
      totalPlayers,
      totalTeams,
      unassignedPlayers,
      playerCapacity,
      teamCapacity,
      progress,
      revenue,
      revenueCapacity,
      revenueProgress,
    };
  }, [playerRows]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);

  const formatDate = (date) =>
    new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(
      new Date(date)
    );

  const handleExportToExcel = () => {
    // Prepare data for export - use all playerRows, not filtered
    const exportData = playerRows.map((player) => {
      // Extract registration ID from player ID (format: "4-P1" -> "4" or "REG-001-P1" -> "REG-001")
      const registrationId = player.id.split('-P')[0];
      const registration = registrantRows.find(r => String(r.id) === String(registrationId));
      
      return {
        'Player Name': player.playerLabel || '',
        'Team Name': player.teamName || '',
        'Contact Name': player.contactName || '',
        'Phone': player.phone || '',
        'Email': player.email || '',
        'Contact Email': player.email || '',
        'Comments': player.comments || '',
        'Registration Date': registration?.registeredAt ? formatDate(registration.registeredAt) : '',
        'Number of Players': registration?.players || '',
      };
    });

    // Create a new workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Registrations');

    // Set column widths for better readability
    const maxWidths = {};
    const columnKeys = Object.keys(exportData[0] || {});
    
    // Initialize with header lengths
    columnKeys.forEach((key) => {
      maxWidths[key] = key.length;
    });
    
    // Find maximum content length for each column
    exportData.forEach((row) => {
      columnKeys.forEach((key) => {
        const value = String(row[key] || '');
        maxWidths[key] = Math.max(maxWidths[key] || 0, value.length);
      });
    });
    
    // Set column widths with padding (add 2-3 characters for padding, and set reasonable minimums/maximums)
    worksheet['!cols'] = columnKeys.map((key) => {
      const baseWidth = maxWidths[key] || 10;
      // Add padding and ensure minimum width, with higher max for certain columns
      const minWidth = Math.max(baseWidth + 3, 15);
      // Allow wider columns for email and comments
      const maxWidth = (key === 'Email' || key === 'Contact Email' || key === 'Comments') ? 60 : 40;
      return { wch: Math.min(minWidth, maxWidth) };
    });

    // Generate filename with current date
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `Toke-for-Stroke-Registrations-${dateStr}.xlsx`;

    // Write the file
    XLSX.writeFile(workbook, filename);
  };

  const handleOpenAddPlayerModal = async () => {
    try {
      const activeEvent = await getActiveEvent();
      if (activeEvent) {
        setAddPlayerForm({
          eventId: activeEvent.id,
          teamId: '',
          contactName: '',
          email: '',
          phone: '',
          comments: '',
        });
        setAddPlayerErrors({});
        setShowAddPlayerModal(true);
      }
    } catch (err) {
      console.error('Error fetching active event:', err);
      alert('Failed to load event information. Please try again.');
    }
  };

  const handleCloseAddPlayerModal = () => {
    setShowAddPlayerModal(false);
    setAddPlayerForm({
      eventId: null,
      teamId: '',
      contactName: '',
      email: '',
      phone: '',
      comments: '',
    });
    setAddPlayerErrors({});
  };

  const handleAddPlayerFormChange = (field, value) => {
    setAddPlayerForm((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (addPlayerErrors[field]) {
      setAddPlayerErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmitAddPlayer = async (e) => {
    e.preventDefault();
    
    // Validation
    const errors = {};
    if (!addPlayerForm.contactName.trim()) {
      errors.contactName = 'Contact name is required';
    }
    if (!addPlayerForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addPlayerForm.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!addPlayerForm.teamId) {
      errors.teamId = 'Team is required';
    }
    if (!addPlayerForm.eventId) {
      errors.eventId = 'Event ID is missing';
    }

    if (Object.keys(errors).length > 0) {
      setAddPlayerErrors(errors);
      return;
    }

    setAddPlayerSubmitting(true);
    setAddPlayerErrors({});

    try {
      const playerData = {
        eventId: addPlayerForm.eventId,
        teamId: parseInt(addPlayerForm.teamId, 10),
        contactName: addPlayerForm.contactName.trim(),
        email: addPlayerForm.email.trim(),
        phone: addPlayerForm.phone?.trim() || null,
        comments: addPlayerForm.comments?.trim() || null,
      };

      await addPlayerToTeam(addPlayerForm.teamId, playerData);
      
      // Refresh data from server
      const activeEvent = await getActiveEvent();
      if (activeEvent) {
        const registrations = await getRegistrations(activeEvent.id);
        processRegistrations(registrations || []);
        
        // Refresh teams
        const teams = await getAvailableTeams(activeEvent.id);
        const teamNames = teams.map(team => team.name);
        setAvailableTeamNames(teamNames);
        setTeamsList(teams);
        const nameToIdMap = teams.reduce((acc, team) => {
          if (team.name && team.id) {
            acc[team.name] = team.id;
          }
          return acc;
        }, {});
        setTeamNameToIdMap(nameToIdMap);
      }

      handleCloseAddPlayerModal();
      alert('Player added successfully!');
    } catch (err) {
      console.error('Error adding player:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to add player. Please try again.';
      setAddPlayerErrors({ submit: errorMessage });
    } finally {
      setAddPlayerSubmitting(false);
    }
  };

  const handleDeletePlayer = async (playerId) => {
    // Extract registration ID from player ID
    const registrationId = String(playerId.split('-P')[0]);
    
    // Find the registration row
    const registrationRow = registrantRows.find((row) => String(row.id) === registrationId);
    if (!registrationRow) {
      console.error('Registration not found for player:', playerId);
      return;
    }

    // Confirm deletion
    const playerName = playerRows.find((p) => p.id === playerId)?.playerLabel || registrationRow.contactName;
    const playerCount = registrationRow.players || 1;
    const confirmMessage = playerCount > 1
      ? `Are you sure you want to delete ${playerName}? This will delete the entire registration (${playerCount} player${playerCount > 1 ? 's' : ''}).`
      : `Are you sure you want to delete ${playerName}? This will permanently delete this registration.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Delete the registration
      await deleteRegistration(registrationId);
      
      // Refresh data from server
      const activeEvent = await getActiveEvent();
      if (activeEvent) {
        const registrations = await getRegistrations(activeEvent.id);
        processRegistrations(registrations || []);
      }
    } catch (err) {
      console.error('Error deleting registration:', err);
      alert('Failed to delete registration. Please try again.');
    }
  };

  const handlePlayerTeamInput = (playerId, value) => {
    setPlayerTeamInputs((prev) => ({ ...prev, [playerId]: value }));
  };

  const handlePlayerTeamSave = async (playerId) => {
    console.log('handlePlayerTeamSave called with playerId:', playerId);
    console.log('playerTeamInputs:', playerTeamInputs);
    console.log('teamPlayers:', teamPlayers);
    
    // Extract registration ID from player ID
    const registrationId = String(playerId.split('-P')[0]);
    console.log('Extracted registrationId:', registrationId);
    
    // Find the registration row
    const registrationRow = registrantRows.find((row) => String(row.id) === registrationId);
    if (!registrationRow) {
      console.error('Registration not found for player:', playerId, 'registrationId:', registrationId);
      setPlayerErrors((prev) => ({ ...prev, '': 'Registration not found' }));
      return;
    }
    
    // Get current team from registration or playerRows
    let currentTeam = registrationRow.teamName || '';
    
    // Try to find player in teamPlayers (might not exist if unassigned)
    let playerObj = null;
    if (currentTeam) {
      const players = teamPlayers[currentTeam] || [];
      playerObj = players.find((p) => p.id === playerId);
    }
    
    // If not found in teamPlayers, create player object from registration
    if (!playerObj) {
      // Find player in playerRows to get their name
      const playerRow = playerRows.find((p) => p.id === playerId);
      playerObj = {
        id: playerId,
        name: playerRow?.playerLabel || registrationRow.contactName || 'Unknown Player',
      };
    }

    console.log('Found currentTeam:', currentTeam, 'playerObj:', playerObj);

    const desiredTeam = (playerTeamInputs[playerId] !== undefined ? playerTeamInputs[playerId] : currentTeam).trim();
    console.log('desiredTeam:', desiredTeam, 'currentTeam:', currentTeam);
    
    if (!desiredTeam) {
      console.log('No team name provided');
      setPlayerErrors((prev) => ({ ...prev, [currentTeam]: 'Team name is required' }));
      return;
    }

    if (desiredTeam === currentTeam) {
      console.log('Team unchanged, no update needed');
      setPlayerErrors((prev) => ({ ...prev, [currentTeam]: '' }));
      return;
    }

    const destCount = (teamPlayers[desiredTeam] || []).length;
    console.log('Destination team count:', destCount);
    if (destCount >= 4) {
      console.log('Destination team is full');
      setPlayerErrors((prev) => ({ ...prev, [desiredTeam]: 'Limit reached: max 4 players' }));
      return;
    }

    console.log('Updating registration:', registrationId, 'from team:', currentTeam || '(unassigned)', 'to team:', desiredTeam);

    // Update local state optimistically
    setTeamPlayers((prev) => {
      const next = { ...prev };
      next[currentTeam] = (next[currentTeam] || []).filter((p) => p.id !== playerId);
      next[desiredTeam] = [...(next[desiredTeam] || []), playerObj];
      return next;
    });

    // Update the registration's team name in local state
    const teamId = desiredTeam ? teamNameToIdMap[desiredTeam] || null : null;
    setRegistrantRows((prev) =>
      prev.map((row) => (row.id === registrationId ? { ...row, teamName: desiredTeam, teamId } : row))
    );

    // Update API
    try {
      console.log('=== CALLING API ===');
      console.log('Registration ID:', registrationId);
      console.log('Update data:', { teamName: desiredTeam || null, teamId: teamId });
      console.log('Full registration row:', registrationRow);
      
      const response = await updateRegistration(registrationId, {
        teamName: desiredTeam || null,
        teamId: teamId,
      });
      console.log('API Response:', response);
      console.log('Successfully updated registration');
      
      // Refresh data from server to ensure consistency
      const activeEvent = await getActiveEvent();
      if (activeEvent) {
        const registrations = await getRegistrations(activeEvent.id);
        processRegistrations(registrations || []);
      }
      
      setPlayerErrors((prev) => ({ ...prev, [currentTeam]: '', [desiredTeam]: '' }));
      // Remove the input state so it falls back to the updated player.teamName
      setPlayerTeamInputs((prev) => {
        const next = { ...prev };
        delete next[playerId];
        return next;
      });
    } catch (err) {
      console.error('Error updating registration:', err);
      console.error('Error details:', err.response?.data || err.message);
      // Revert local state on error
      setTeamPlayers((prev) => {
        const next = { ...prev };
        next[desiredTeam] = (next[desiredTeam] || []).filter((p) => p.id !== playerId);
        next[currentTeam] = [...(next[currentTeam] || []), playerObj];
        return next;
      });
      const previousTeamId = registrationRow.teamId;
      setRegistrantRows((prev) =>
        prev.map((row) => (row.id === registrationId ? { ...row, teamName: currentTeam, teamId: previousTeamId } : row))
      );
      setPlayerErrors((prev) => ({
        ...prev,
        [currentTeam]: 'Failed to update team name. Please try again.',
        [desiredTeam]: '',
      }));
    }
  };

  if (loading) {
    return (
      <div className="admin-portal">
        <div className="admin-hero">
          <div>
            <p className="eyebrow">Admin Portal</p>
            <h1>Welcome back, {user?.username || 'Admin'}</h1>
            <p className="subhead">Loading registrations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-portal">
      <div className="admin-hero">
        <div>
          <p className="eyebrow">Admin Portal</p>
          <h1>Welcome back, {user?.username || 'Admin'}</h1>
          <p className="subhead">Clean snapshot of registrations, revenue, and teams.</p>
        </div>
        <div className="chip">Toke for Stroke Open • 2026</div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Registered</div>
          <div className="stat-value">
            {metrics.totalPlayers}
            <span className="stat-muted"> / {metrics.playerCapacity} players</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${metrics.progress}%` }} />
          </div>
          <div className="stat-foot">{metrics.totalPlayers} players locked in</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Revenue</div>
          <div className="stat-value">
            {formatCurrency(metrics.revenue)}
            <span className="stat-muted"> / {formatCurrency(metrics.revenueCapacity)}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${metrics.revenueProgress}%` }} />
          </div>
          <div className="stat-foot">
            Calculated at $180 per player
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Teams</div>
          <div className="stat-value">
            {metrics.totalTeams}
            <span className="stat-muted"> / {metrics.teamCapacity} teams</span>
          </div>
          <div className="stat-foot">Unique team names registered</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Unassigned</div>
          <div className="stat-value">{metrics.unassignedPlayers}</div>
          <div className="stat-foot">Players without a team</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <div>
            <h2>All players & teams</h2>
          </div>
          <div className="panel-actions">
            <button 
              className="refresh-btn add-player-btn" 
              type="button" 
              onClick={handleOpenAddPlayerModal}
              style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)', color: '#ffffff', border: 'none' }}
            >
              <span className="refresh-icon">+</span>
              Add Player
            </button>
            <button 
              className="refresh-btn" 
              type="button" 
              onClick={async () => {
                try {
                  setLoading(true);
                  const activeEvent = await getActiveEvent();
                  if (activeEvent) {
                    const registrations = await getRegistrations(activeEvent.id);
                    processRegistrations(registrations || []);
                  }
                } catch (err) {
                  console.error('Error refreshing data:', err);
                } finally {
                  setLoading(false);
                }
              }}
            >
              <span className="refresh-icon">⟳</span>
              Refresh
            </button>
            <button 
              className="refresh-btn" 
              type="button" 
              onClick={handleExportToExcel}
              style={{ marginLeft: '0.5rem' }}
            >
              <span className="refresh-icon">📥</span>
              Export to Excel
            </button>
          </div>
        </div>

        <div className="panel-filters">
          <div className="filter-group">
            <label htmlFor="team-filter" className="filter-label">Filter by Team:</label>
            <select
              id="team-filter"
              className="filter-select"
              value={selectedTeamFilter}
              onChange={(e) => setSelectedTeamFilter(e.target.value)}
            >
              <option value="all">All Teams</option>
              <option value="unassigned">Unassigned Players</option>
              {uniqueTeams.map((team) => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="search-filter" className="filter-label">Search:</label>
            <input
              id="search-filter"
              type="text"
              className="filter-input"
              placeholder="Search players, teams, contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {(selectedTeamFilter !== 'all' || searchQuery.trim()) && (
            <button
              className="clear-filters-btn"
              type="button"
              onClick={() => {
                setSelectedTeamFilter('all');
                setSearchQuery('');
              }}
            >
              Clear Filters
            </button>
          )}
        </div>

        <div className="table-wrapper">
          <table className="registrants-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Team (editable)</th>
                <th>Contact</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Contact Email</th>
                <th>Comments</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedPlayerRows.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    No players found matching your filters.
                  </td>
                </tr>
              ) : (
                filteredAndSortedPlayerRows.map((player) => (
                <tr key={player.id}>
                  <td>
                    <div className={`contact-name ${!player.teamName ? 'no-team' : ''}`}>{player.playerLabel}</div>
                  </td>
                  <td>
                    <div className="team-cell">
                      <div className="avatar">{(playerTeamInputs[player.id] || player.teamName || 'U').slice(0, 1)}</div>
                      <div className="team-edit">
                        {(() => {
                          // Use input value if it exists, otherwise fall back to the player's current team name
                          const selectedTeam = playerTeamInputs[player.id] !== undefined ? playerTeamInputs[player.id] : (player.teamName || '');
                          const isDirty = selectedTeam !== (player.teamName || '');
                          return (
                            <div className="team-edit-row">
                              <select
                                className="team-input"
                                value={selectedTeam}
                                onChange={(e) => handlePlayerTeamInput(player.id, e.target.value)}
                                aria-label="Edit team name"
                              >
                                <option value="">No team</option>
                                {getSelectableTeams(player.teamName).map((name) => (
                                  <option key={name} value={name}>
                                    {name}
                                  </option>
                                ))}
                              </select>
                              <button
                                className="save-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  console.log('Save button clicked for player:', player.id);
                                  console.log('isDirty:', isDirty);
                                  console.log('selectedTeam:', selectedTeam);
                                  console.log('player.teamName:', player.teamName);
                                  handlePlayerTeamSave(player.id);
                                }}
                                type="button"
                                title="Save team"
                                disabled={!isDirty}
                              >
                                Save
                              </button>
                            </div>
                          );
                        })()}
                        {player.teamName && (
                          <div className="muted team-count">
                            Team total: {teamPlayerCounts[player.teamName] || 0} / 4
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="contact-name">{player.contactName}</td>
                  <td className="muted">{player.phone}</td>
                  <td className="muted">{player.email}</td>
                  <td className="muted">{player.email}</td>
                  <td className="muted truncate">{player.comments}</td>
                  <td>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeletePlayer(player.id)}
                      type="button"
                      title="Delete player"
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                    >
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 16 16" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ flexShrink: 0 }}
                      >
                        <path 
                          d="M5.5 5.5C5.77614 5.5 6 5.72386 6 6V12C6 12.2761 5.77614 12.5 5.5 12.5C5.22386 12.5 5 12.2761 5 12V6C5 5.72386 5.22386 5.5 5.5 5.5Z" 
                          fill="white"
                        />
                        <path 
                          d="M8 5.5C8.27614 5.5 8.5 5.72386 8.5 6V12C8.5 12.2761 8.27614 12.5 8 12.5C7.72386 12.5 7.5 12.2761 7.5 12V6C7.5 5.72386 7.72386 5.5 8 5.5Z" 
                          fill="white"
                        />
                        <path 
                          d="M10.5 6C10.5 5.72386 10.7239 5.5 11 5.5C11.2761 5.5 11.5 5.72386 11.5 6V12C11.5 12.2761 11.2761 12.5 11 12.5C10.7239 12.5 10.5 12.2761 10.5 12V6Z" 
                          fill="white"
                        />
                        <path 
                          fillRule="evenodd" 
                          clipRule="evenodd" 
                          d="M10.5 2H5.5C4.94772 2 4.5 2.44772 4.5 3V4H2.5C2.22386 4 2 4.22386 2 4.5C2 4.77614 2.22386 5 2.5 5H3V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V5H13.5C13.7761 5 14 4.77614 14 4.5C14 4.22386 13.7761 4 13.5 4H11.5V3C11.5 2.44772 11.0523 2 10.5 2ZM5.5 3H10.5V4H5.5V3ZM12 5H4V13H12V5Z" 
                          fill="white"
                        />
                      </svg>
                      <span>Delete</span>
                    </button>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Player Modal */}
      {showAddPlayerModal && (
        <div className="modal-overlay" onClick={handleCloseAddPlayerModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Player to Team</h2>
              <button 
                className="modal-close-btn" 
                onClick={handleCloseAddPlayerModal}
                type="button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmitAddPlayer} className="add-player-form">
              <div className="form-group">
                <label htmlFor="team-select" className="form-label">
                  Team <span className="required">*</span>
                </label>
                <select
                  id="team-select"
                  className={`form-input ${addPlayerErrors.teamId ? 'form-input-error' : ''}`}
                  value={addPlayerForm.teamId}
                  onChange={(e) => handleAddPlayerFormChange('teamId', e.target.value)}
                  required
                >
                  <option value="">Select a team</option>
                  {teamsList.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
                {addPlayerErrors.teamId && (
                  <div className="error-text">{addPlayerErrors.teamId}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="contact-name" className="form-label">
                  Contact Name <span className="required">*</span>
                </label>
                <input
                  id="contact-name"
                  type="text"
                  className={`form-input ${addPlayerErrors.contactName ? 'form-input-error' : ''}`}
                  value={addPlayerForm.contactName}
                  onChange={(e) => handleAddPlayerFormChange('contactName', e.target.value)}
                  placeholder="Enter contact name"
                  required
                />
                {addPlayerErrors.contactName && (
                  <div className="error-text">{addPlayerErrors.contactName}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email <span className="required">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className={`form-input ${addPlayerErrors.email ? 'form-input-error' : ''}`}
                  value={addPlayerForm.email}
                  onChange={(e) => handleAddPlayerFormChange('email', e.target.value)}
                  placeholder="Enter email address"
                  required
                />
                {addPlayerErrors.email && (
                  <div className="error-text">{addPlayerErrors.email}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Phone
                </label>
                <input
                  id="phone"
                  type="tel"
                  className="form-input"
                  value={addPlayerForm.phone}
                  onChange={(e) => handleAddPlayerFormChange('phone', e.target.value)}
                  placeholder="Enter phone number (optional)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="comments" className="form-label">
                  Comments
                </label>
                <textarea
                  id="comments"
                  className="form-input form-textarea"
                  value={addPlayerForm.comments}
                  onChange={(e) => handleAddPlayerFormChange('comments', e.target.value)}
                  placeholder="Enter any comments (optional)"
                  rows="3"
                />
              </div>

              {addPlayerErrors.submit && (
                <div className="error-text" style={{ marginBottom: '1rem' }}>
                  {addPlayerErrors.submit}
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={handleCloseAddPlayerModal}
                  disabled={addPlayerSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-submit-btn"
                  disabled={addPlayerSubmitting}
                >
                  {addPlayerSubmitting ? 'Adding...' : 'Add Player'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminPortal;

